import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Single source of truth for landing-page external URLs (X, Discord, Mint,
 * Magic Eden, Tensor). Any component that renders one of those CTAs should
 * pull from here — never hardcode a URL again.
 *
 * The context fetches once on mount and refetches on demand via `refresh()`.
 */
const LandingSettingsContext = createContext(null);

const EMPTY = {
  twitter_url: "",
  discord_url: "",
  mint_url: "",
  magiceden_url: "",
  tensor_url: "",
};

export function LandingSettingsProvider({ children }) {
  const [settings, setSettings] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/landing/settings");
      setSettings({ ...EMPTY, ...data });
    } catch (_) { /* keep last known */ }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <LandingSettingsContext.Provider value={{ settings, loaded, refresh }}>
      {children}
    </LandingSettingsContext.Provider>
  );
}

export function useLandingSettings() {
  const ctx = useContext(LandingSettingsContext);
  return ctx || { settings: EMPTY, loaded: false, refresh: () => {} };
}

/** Returns the URL for a given key, or "#" if not set (so links stay clickable but no-op visually). */
export function useLandingUrl(key) {
  const { settings } = useLandingSettings();
  return settings?.[key] || "#";
}

/**
 * Returns true only for a non-empty, trimmed http(s) URL. Every dynamic
 * landing-page CTA uses this to decide whether to render a real anchor or
 * an inert (non-navigating) element.
 */
export function isUsableUrl(u) {
  return typeof u === "string" && /^https?:\/\/\S+$/i.test(u.trim());
}
